import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Logo from '@/components/logo';
import React from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, TrendingUp, BarChart3, Target, Wallet, Shield, Zap } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginWithEmail, loading, user } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      const msg = 'Please enter both email and password.';
      setFormError(msg);
      toast({
        title: 'Missing Information',
        description: msg,
        variant: 'destructive',
      });
      return;
    }

    setFormError(null);
    setIsSubmitting(true);
    try {
      await loginWithEmail(email, password);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error details:', error);
      
      let errorMessage = 'Could not log you in. Please try again.';
      
      if (error?.message) {
        if (error.message.includes('Failed to fetch') || error.message.includes('fetch')) {
          errorMessage = 'Unable to connect to authentication service. Please check your internet connection and try again.';
        } else if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Please confirm your email address before logging in.';
        } else {
          errorMessage = error.message;
        }
      } else if (error?.status === 0) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error?.name === 'AuthApiError') {
        errorMessage = 'Authentication service error. Please try again later.';
      }
      
      setFormError(errorMessage);
      toast({
        title: 'Login Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || user) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex min-h-screen flex-col lg:flex-row">
        {/* Left Panel - Feature Showcase */}
        <div className="hidden lg:flex lg:flex-1 relative bg-card">
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col justify-between px-16 py-12 text-white">
            {/* Logo & Brand */}
            <div>
              <div className="flex items-center gap-3 mb-16">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-foreground" />
                </div>
                <span className="text-xl font-semibold">KwachaLite</span>
              </div>
              
              <div className="max-w-md">
                <h1 className="text-4xl font-bold mb-4 leading-tight">
                  Welcome Back
                </h1>
                <p className="text-lg text-muted-foreground mb-12">
                  Sign in to access your financial dashboard and continue managing your finances with confidence.
                </p>
              </div>
            </div>
            
            {/* Features List */}
            <div className="max-w-md space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 border border-border">
                  <BarChart3 className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Advanced Analytics</h3>
                  <p className="text-sm text-muted-foreground">Track spending patterns with detailed charts and reports.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 border border-border">
                  <Target className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Goal Management</h3>
                  <p className="text-sm text-muted-foreground">Set and track financial goals with real-time progress updates.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 border border-border">
                  <Shield className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Secure Platform</h3>
                  <p className="text-sm text-muted-foreground">Your data is protected with enterprise-grade encryption.</p>
                </div>
              </div>
            </div>
            
            {/* Footer text */}
            <div className="max-w-md border-t border-slate-800 pt-8">
              <p className="text-sm text-muted-foreground">
                Trusted by professionals to manage their financial future
              </p>
            </div>
          </div>
        </div>
        
        {/* Right Panel - Login Form */}
        <div className="flex-1 lg:max-w-xl flex items-center justify-center p-4 sm:p-6 lg:p-12 bg-white">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-6 sm:mb-8">
              <div className="flex justify-center mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <h1 className="text-lg sm:text-xl font-semibold text-foreground">KwachaLite</h1>
            </div>
            
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Sign In</h2>
              <p className="text-sm sm:text-base text-muted-foreground">Enter your credentials to access your account</p>
            </div>
            
            <form className="space-y-4 sm:space-y-5" onSubmit={handleLogin}>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 sm:h-11 border-slate-300 focus:border-slate-900 focus:ring-slate-900 text-base"
                  autoComplete="email"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">
                    Password
                  </Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 sm:h-11 border-slate-300 focus:border-slate-900 focus:ring-slate-900 pr-12 text-base"
                    autoComplete="current-password"
                    aria-describedby={formError ? 'login-error' : undefined}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 sm:h-9 sm:w-9 hover:bg-muted"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    )}
                  </Button>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 sm:h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-colors disabled:opacity-70 text-base" 
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            {formError && (
              <div id="login-error" role="alert" aria-live="polite" className="mt-4 p-3 text-sm text-destructive bg-destructive/10 rounded-lg border border-destructive/20">
                {formError}
              </div>
            )}
            
            <div className="mt-6 space-y-4">
              <div className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/signup" className="text-foreground hover:underline font-medium">
                  Sign up
                </Link>
              </div>
              
              <div className="text-center text-xs text-slate-500 pt-4 border-t border-slate-200">
                By signing in, you agree to our{' '}
                <button className="text-slate-900 hover:underline">Terms of Service</button>
                {' '}and{' '}
                <button className="text-slate-900 hover:underline">Privacy Policy</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}